import {booleanAttribute, ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, ElementRef, HostBinding, Input, OnDestroy, OnInit, Self, signal, ViewChild} from "@angular/core";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {erIkkeNil, erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {AbstractControl, ControlValueAccessor, FormControl, FormGroup, FormsModule, NgControl, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators} from "@angular/forms";
import {MatFormFieldControl} from "@angular/material/form-field";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {Subject, tap} from "rxjs";
import {BooleanInput, coerceBooleanProperty} from "@angular/cdk/coercion";
import {Mobilnummer} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {MatOptionModule} from "@angular/material/core";
import {MatSelectModule} from "@angular/material/select";
import {TranslateModule} from "@ngx-translate/core";
import {MatInputModule} from "@angular/material/input";
import {Temporal} from "temporal-polyfill";
import {GjeldendeTidssone} from "felles-ng2/tjenester/i18n/GjeldendeTidssone";
import {KlokkeslettVelgerKomponent} from "../klokkeslett-velger/klokkeslett-velger";
import {MatDatepicker, MatDatepickerInput, MatDatepickerToggle} from "@angular/material/datepicker";
import {mapKeys} from "radash";
import {erIkkeTomtObjekt} from "felles-ng2/støttefunksjoner/objekter";
import {første} from "felles-ng2/støttefunksjoner/lister";
import {i18n, Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {MatIcon} from "@angular/material/icon";
import {erLikeZonedDateTime} from "felles-ng2/støttefunksjoner/temporal";
import {PlainDateDateAdapter} from "felles-ng2/infrastruktur/material/PlainDateDateAdapter";
import ZonedDateTime = Temporal.ZonedDateTime;
import PlainDate = Temporal.PlainDate;
import PlainTime = Temporal.PlainTime;
import {erIkkeTomStreng} from "felles-ng2/støttefunksjoner/strenger";

const debug = initDebugger('DatoKlokkeslettVelgerKomponent');

export type SkjemaType = DatoKlokkeslettVelgerKomponent['skjema'];
export type SkjemaVerdier = SkjemaType['value'];

/**
 * Viser skjema for angivelse av dato med klokkeslett.
 *
 * Eksponerer valgt verdi som {@link ZonedDateTime} fra temporal APIet
 *
 * Fungerer med Ng template-basert form ([(ngModel)]="...") og Ng reactive form ([formControl]="...").
 * Fungerer med <mat-form-field> fra Material.
 *
 * Se {@link #feilmeldingstekst()} for oversikt over eksponerte feilmeldingsnøkler.
 */
@Component({
    selector: 'dato-klokkeslett-velger',
    templateUrl: './dato-klokkeslett-velger.html',
    styleUrls: ['./dato-klokkeslett-velger.scss'],
    providers: [{provide: MatFormFieldControl, useExisting: DatoKlokkeslettVelgerKomponent}],
    imports: [
        FormsModule,
        ReactiveFormsModule,
        MatInputModule,
        MatSelectModule,
        MatOptionModule,
        TranslateModule,
        KlokkeslettVelgerKomponent,
        MatDatepicker,
        MatDatepickerInput,
        MatDatepickerToggle,
        MatIcon
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DatoKlokkeslettVelgerKomponent implements OnInit, OnDestroy, ControlValueAccessor, MatFormFieldControl<ZonedDateTime> {

    /** Referanse til datofelt, ikke satt før ngAfterViewInit.*/
    @ViewChild('datofelt') datofelt!: ElementRef<HTMLInputElement>;
    /** Referanse til MAT dato-velger, ikke satt før ngAfterViewInit.*/
    @ViewChild(MatDatepicker) datovelger!: MatDatepicker<any>;
    /** Referanse til <klokkeslett-velger>, ikke satt før ngAfterViewInit.*/
    @ViewChild(KlokkeslettVelgerKomponent) klokkeslettvelger!: KlokkeslettVelgerKomponent;

    skjema = new FormGroup({
        dato: new FormControl(<PlainDate | null>null, {validators: Validators.required}),
        klokkeslett: new FormControl(<PlainTime | null>null, {validators: Validators.required}),
    });

    private datoMedKlokkeslett = signal<ZonedDateTime | null>(null);

    constructor(/** skjemakontrollen tilknyttet denne komponenten via formControl eller ngModel direktiver*/
                @Self() private tilknyttetSkjemakontroll: NgControl,
                private destroyRef: DestroyRef,
                private cdRef: ChangeDetectorRef,
                private gjeldendeTidssone: GjeldendeTidssone,
                private oversettelser: Oversettelser,
                private matDatoAdapter: PlainDateDateAdapter) {
        // setter valueAccessor direkte fremfor via DI og providers i @Component med `{provide: NG_VALUE_ACCESSOR, multi:true, useExisting: DatoKlokkeslettVelgerKomponent}`
        // for å unngå sirkulær avhengighet.
        this.tilknyttetSkjemakontroll.valueAccessor = this;

        this.lyttTilSkjemaEndringer();
    }

    get tidssone() {
        return this.gjeldendeTidssone.brukersTidssone();
    }

    vedFokusMottatt() {
        if (erIkkeNil(this.vedFeltBerørt)) this.vedFeltBerørt();

        //MatFormFieldControl relatert
        if (!this.focused) {
            this.focused = true;
            this.stateChanges.next(); //signaliser til mat-form-field at verdi kan ha blitt endret
        }
    }

    vedFokusMistet() {
        //MatFormFieldControl relatert
        //vi venter litt med focused = false slik at bruker får anledning til å klikke inn i klokkeslett-feltet
        //dersom dato-feltet er tomt vil focused = false føre til at klokkeskett-feltet blir skjult og label ikke lenger flyter
        setTimeout(() => {
            this.focused = false;
            //if (erIkkeNil(this.vedFeltBerørt)) this.vedFeltBerørt();
            this.stateChanges.next(); //signaliser til mat-form-field at verdi kan ha blitt endret
            //uten denne blir ikke datofeltPlaceholder() kalt og dermed forblir placeholder på dato-input-feltet synlig (har forsøkt med signal
            //har forsøkt med signal som settes i shouldLabelFloat() metoden - men det gir feilmelding av typen 'kan ikke skrive til signal inne i en computed/effect
            this.cdRef.markForCheck();
        }, 1000);
    }

    /** Returnerer aktuelt dato-format brukt på matDatepicker input-felt for bruk som placeholder.
     * MERK at vi ikke benytter MatDatePickerPlaceholderDirektiv da vi trenger å styre hvorvidt placeholder skal vises eller ei. */
    get datofeltPlaceholder() {
        return !this.shouldLabelFloat ? '' : this.matDatoAdapter.localeSensitivtParseFormat().toLowerCase();
    }

    harFeil() {
        return erIkkeTomtObjekt(this.tilknyttetSkjemakontroll.errors)
    }

    foersteFeil() {
        //fom ES2015 garanteres rekkefølge på object properties til å være insert-rekkefølge, dermed får vi feil fra dato før klokkeslett
        //MEN - merk at dersom tilknyttetSkjemakontroll er markert som required vil 'required'-feilnøkkelen ligge aller først
        return første(Object.entries(this.tilknyttetSkjemakontroll.errors!)
          .map(førsteFeil => this.feilmeldingstekst(...førsteFeil))
          .filter(melding => erIkkeTomStreng(melding)));
    }

    //private hjelpemetoder

    private setDatoMedKlokkeslett(datoMedKlokkeslett: ZonedDateTime | null) {
        if (erNil(datoMedKlokkeslett)) {
            this.skjema.setValue({dato: null, klokkeslett: null});
            return;
        }
        const datoMedKlokkeslettIBrukersTidssone = datoMedKlokkeslett.withTimeZone(this.tidssone.somZoneId());
        //for å unngå rekursjon avbryter vi oppd av skjema dersom angitt verdi ikke repr noen endring
        if (erLikeZonedDateTime(datoMedKlokkeslettIBrukersTidssone, this.datoMedKlokkeslett())) {
            debug('setDatoMedKlokkeslett, ny verdi %s representerer ingen endring i forhold til eksisterende %s', datoMedKlokkeslett?.toString(), this.datoMedKlokkeslett()?.toString());
            return;
        }
        this.skjema.setValue({
            dato: datoMedKlokkeslettIBrukersTidssone.toPlainDate(),
            klokkeslett: datoMedKlokkeslettIBrukersTidssone.toPlainTime()
        });
    }

    private lyttTilSkjemaEndringer() {
        this.skjema.valueChanges
          .pipe(
            takeUntilDestroyed(this.destroyRef),
            tap(nummer => this.propagerValideringsfeilTilTilknyttetSkjemakontroll()),
          )
          .subscribe(skjemaverdier => {
              debug("skjema endret, skjemaverdier:%j, valid:%s ", skjemaverdier, this.skjema.valid)

              //eksponerer null dersom skjema er ugyldig, ellers datoMedKlokkeslett
              this.datoMedKlokkeslett.set(this.beregnEksponertVerdi(skjemaverdier));
              debug("eksponerer verdi %s, skjema.valid: %s", this.datoMedKlokkeslett()?.toString(), this.skjema.valid)
              if (erIkkeNil(this.vedEndring)) this.vedEndring(this.datoMedKlokkeslett());
          });
    }

    private beregnEksponertVerdi(skjemaverdier: SkjemaVerdier): ZonedDateTime | null {
        if (this.skjema.invalid) return null;
        if (erNil(skjemaverdier.dato)) return null;
        if (erNil(skjemaverdier.klokkeslett)) return null;
        return skjemaverdier.dato.toZonedDateTime({plainTime: skjemaverdier.klokkeslett, timeZone: this.tidssone.somZoneId()});
    }

    /** Sørger for at tilknyttet skjemakontroll kjører validering, som i sin tur vil kalle {@link #tilknyttetSkjemakontrollValidator} */
    private propagerValideringsfeilTilTilknyttetSkjemakontroll() {
        this.tilknyttetSkjemakontroll.control?.updateValueAndValidity({onlySelf: true, emitEvent: false})
    }

    /** Returnerer valideringsfeil registrert på {@link #skjema}.
     * Se også {@link #propagerValideringsfeilTilTilknyttetSkjemakontroll}. */
    private tilknyttetSkjemakontrollValidator(): ValidatorFn {
        return (control: AbstractControl<Mobilnummer>): ValidationErrors | null => {
            //les valideringsfeil fra feltKontroll og returner disse
            const feil = {
                ...mapKeys(this.skjema.controls.dato.errors ?? {}, nøkkel => `dato-${nøkkel}`),
                ...mapKeys(this.skjema.controls.klokkeslett.errors ?? {}, nøkkel => `klokkeslett-${nøkkel}`),
            };
            debug("tilknyttetSkjemakontrollValidator, skjema.valid:%s, feil: %j", this.skjema.valid, feil)
            return feil;
        }
    }

    private feilmeldingstekst(nøkkel: string, verdier: any) {
        switch (nøkkel) {
            case 'dato-required':
                return this.oversettelser.oversett(i18n('Dato må angis, på formen {format}'), {format: this.datofeltPlaceholder});
            case 'dato-matDatepickerParse':
                return this.oversettelser.oversett(i18n('Ugyldig datoformat, forventet dd.mm.yyyy'));
            case 'klokkeslett-required':
                return this.oversettelser.oversett(i18n('Klokkeslett må angis, på formen hh:mm'));
            case 'klokkeslett-pattern':
                return this.oversettelser.oversett(i18n('Ugyldig klokkeslettformat, forventet hh:mm'));
        }
    }

    //ng2 livssykelmetoder

    ngOnInit(): void {
        //sørger for at valideringsfeil knyttet til det lokale skjemaet propagerer til skjemakontroll tilknyttet denne komponenten
        //kan ikke gjøres i konstruktør da tilknyttetSkjemakontroll.control ikke er satt enda
        this.tilknyttetSkjemakontroll.control!.addValidators(this.tilknyttetSkjemakontrollValidator())
    }

    ngOnDestroy() {
        this.stateChanges.complete();
    }


    //ControlValueAccessor implementasjon

    /** Settes via {@link #registerOnChange} (ifbm ControlValueAccessor)*/
    private vedEndring: Function | null = null;

    /** Settes via {@link #registerOnTouched} (ifbm ControlValueAccessor)*/
    private vedFeltBerørt: Function | null = null;

    registerOnChange(vedEndring: Function): void {
        this.vedEndring = vedEndring;
    }

    registerOnTouched(vedBerørt: Function): void {
        this.vedFeltBerørt = vedBerørt;
    }

    setDisabledState(isDisabled: boolean): void {
        debug("setDisabledState", isDisabled)
        if (isDisabled) this.skjema.disable();
        else this.skjema.enable();
    }

    writeValue(datoMedKlokkeslett: ZonedDateTime | null): void {
        debug("writeValue %j", datoMedKlokkeslett)
        this.setDatoMedKlokkeslett(datoMedKlokkeslett)
        this.stateChanges.next(); //signaliser til mat-form-field at verdi kan ha blitt endret
    }

    //MatFormFieldControl implementasjon

    /** brukes til å lage unike IDer, se {@link #id}*/
    static nesteId = 0;

    private _placeholder: string = '';
    private _required = false;

    @Input('aria-describedby') userAriaDescribedBy!: string; //@todo hva gjør vi med denne?

    get placeholder() {
        return this.shouldLabelFloat ? this._placeholder : '';
    }

    @Input() set placeholder(plh) {
        this._placeholder = plh;
        this.stateChanges.next(); //signaliser til mat-form-field at verdi kan ha blitt endret
    }

    get required() {
        return this._required ||
          (this.tilknyttetSkjemakontroll.control?.hasValidator(Validators.required) ?? false);
    }

    @Input({transform: booleanAttribute}) set required(req: boolean) {
        this._required = req;
        if (req) {
            this.skjema.controls.dato.addValidators(Validators.required);
            this.skjema.controls.klokkeslett.addValidators(Validators.required);
        } else {
            this.skjema.controls.dato.removeValidators(Validators.required);
            this.skjema.controls.klokkeslett.removeValidators(Validators.required);
        }
        this.stateChanges.next(); //signaliser til mat-form-field at verdi kan ha blitt endret
    }

    get disabled(): boolean {
        return this.skjema.disabled
    }

    @Input() set disabled(value: BooleanInput) {
        this.setDisabledState(coerceBooleanProperty(value))
        this.stateChanges.next(); //signaliser til mat-form-field at verdi kan ha blitt endret
    }

    public get ngControl(): NgControl {
        return this.tilknyttetSkjemakontroll;
    }

    readonly stateChanges = new Subject<void>();

    @HostBinding() id = `dato-klokkeslett-velger-${DatoKlokkeslettVelgerKomponent.nesteId++}`;

    controlType = 'dato-klokkeslett-velger';

    /** se {@link #vedFokusMottatt} / {@link #vedFokusMistet} */
    focused = false;

    get value(): ZonedDateTime | null {
        return this.datoMedKlokkeslett();
    }

    set value(datoMedKlokkeslett: ZonedDateTime | null) {
        this.writeValue(datoMedKlokkeslett);
    }

    get empty() {
        return erNil(this.skjema.value.dato) && erNil(this.skjema.value.klokkeslett);
    }

    get shouldLabelFloat() {
        //@todo finnes det noen måte å delegere this.focused til datofeltet?
        return this.focused || !this.empty || this.klokkeslettvelger?.shouldLabelFloat;
    }

    get errorState(): boolean {
        return this.skjema.invalid;
    }

    setDescribedByIds(ids: string[]) {
        if (erNil(this.datofelt)) return;
        const controlElement = this.datofelt.nativeElement;
        controlElement.setAttribute('aria-describedby', ids.join(' '));
    }

    onContainerClick(event: MouseEvent) {
        //console.log('#CLIKC', event.target);
        const målelement = event.target as Element;
        switch (målelement.tagName.toLowerCase()) {
            case 'input':
                return; //trenger ikke å sette fokus dersom klikket var i inputfeltet
            case 'mat-label':
                //klikk på mat label - gi fokus til dato-input-feltet
                this.datofelt.nativeElement.focus();
                return;
            case 'mat-icon':
                //klikk på klokkeslett-ikon - gi fokus til klokkeslettfelt
                this.klokkeslettvelger.onContainerClick(event);
                return;
            case 'span':
                //klikk på tidssoneangivelse - gi fokus til klokkeslettfelt
                if (målelement.classList.contains('tidssone'))
                    this.klokkeslettvelger.onContainerClick(event);
                return;
        }

        //åpner dato-velger
        this.datovelger.open();
    }
}
